import React, { useState, useEffect } from 'react';
import '../styles/AdminUserManager.css';
import AdminSidebar from './AdminSidebar';
import { toast } from "../../utils/toast";
import { adminFetch, adminErrorMessage } from "../../api/adminFetch";

const AdminUserManager = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchUsers = async () => {
        try {
            const res = await adminFetch('/api/admin/users');
            if (!res.ok) {
                toast(await adminErrorMessage(res));
                setUsers([]);
                return;
            }
            const data = await res.json();
            setUsers(data.userList || []);
        } catch (err) {
            console.error("사용자 불러오기 실패", err);
        } finally {
            setIsLoading(false);
            console.log("사용자 목록:", users);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleUserClick = (user) => {
        setSelectedUser(user);
        console.log("선택된 사용자:", user);
    };

    const handleBlockUser = async () => {
        if (selectedUser.grade === 'ADMIN') {
            toast('관리자는 정지할 수 없습니다.');
            return;
        }
        if (selectedUser.grade === 'BLOCKED') {
            toast('이미 정지된 사용자입니다.');
            return;
        }
        if (!selectedUser) {
            toast('사용자를 선택하세요.');
            return;
        }

        if (!window.confirm(`${selectedUser.name}(${selectedUser.loginId}) 사용자를 정지하시겠습니까?`)) {
            return;
        }

        try {
            // 서버는 쿼리 파라미터로 userId 를 받는다. 경로 변수로 보내면 404 다.
            const res = await adminFetch(`/api/admin/user/block?userId=${selectedUser.id}`, {
                method: 'PUT',
            });
            if (res.ok) {
                toast('사용자가 정지되었습니다.');
                fetchUsers();
                setSelectedUser(null);
            } else {
                toast('사용자 정지 실패');
            }
        } catch (err) {
            console.error("사용자 정지 오류", err);
        }
    };

    // 헤더 클릭 정렬 (sc-th--sort)
    const [sort, setSort] = React.useState({ key: null, dir: "asc" });

    const toggleSort = (key) =>
        setSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
        );

    const sortedUsers = React.useMemo(() => {
        if (!sort.key) return users;
        const sign = sort.dir === "asc" ? 1 : -1;
        return [...users].sort((a, b) => {
            const x = a[sort.key], y = b[sort.key];
            if (typeof x === "number" && typeof y === "number") return (x - y) * sign;
            return String(x ?? "").localeCompare(String(y ?? ""), "ko") * sign;
        });
    }, [users, sort]);

    const sortArrow = (key) =>
        sort.key === key ? <span className="sc-sortarrow">{sort.dir === "asc" ? "▲" : "▼"}</span> : null;

    if (isLoading) {
        return (
            <div className="sch-adminPage">
                <AdminSidebar />
                <div className="sch-adminMain">
                    <p className="sc-caption">불러오는 중…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="sch-adminPage">
            <AdminSidebar />

            <div className="sch-adminMain">
                <div className="sch-adminHead">
                    <h2 className="sc-h2">사용자 관리</h2>
                    <button
                        type="button"
                        className="sc-btn sc-btn--danger sc-btn--sm"
                        onClick={handleBlockUser}
                        disabled={!selectedUser}
                    >
                        정지
                    </button>
                </div>

                <div className="sc-tablewrap">
                    <table className="sc-table sc-table--responsive">
                        <thead>
                            <tr>
                                {[
                                    ["name", "이름", false],
                                    ["loginId", "아이디", false],
                                    ["email", "이메일", false],
                                    ["phoneNumber", "전화번호", false],
                                    ["grade", "등급", false],
                                    ["joinDate", "가입일", false],
                                    ["latestLoginDate", "마지막 로그인", false],
                                    ["point", "포인트", true],
                                    ["totalPurchaseAmount", "총 구매액", true],
                                ].map(([key, label, num]) => (
                                    <th
                                        key={key}
                                        className={`sc-th--sort${num ? " sc-td--num" : ""}`}
                                        onClick={() => toggleSort(key)}
                                        aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                                    >
                                        {label}{sortArrow(key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedUsers.map((user) => (
                                <tr
                                    key={user.id}
                                    role="button"
                                    onClick={() => handleUserClick(user)}
                                    className={selectedUser?.id === user.id ? "sc-tr--selected" : ""}
                                >
                                    <td data-label="이름">{user.name}</td>
                                    <td data-label="아이디" className="sc-td--muted">{user.loginId}</td>
                                    <td data-label="이메일" className="sc-td--muted">{user.email}</td>
                                    <td data-label="전화번호" className="sc-td--muted">{user.phoneNumber}</td>
                                    <td data-label="등급">
                                        <span className={`sc-badge ${user.grade === "BLOCKED" ? "sc-badge--canceled" : "sc-badge--done"}`}>
                                            {user.grade}
                                        </span>
                                    </td>
                                    <td data-label="가입일" className="sc-td--muted">{user.joinDate}</td>
                                    <td data-label="마지막 로그인" className="sc-td--muted">{user.latestLoginDate}</td>
                                    <td data-label="포인트" className="sc-td--num">{user.point.toLocaleString()} P</td>
                                    <td data-label="총 구매액" className="sc-td--num">{user.totalPurchaseAmount.toLocaleString()} 원</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {users.length === 0 && (
                        <div className="sc-empty">
                            <div className="sc-empty__title">사용자가 없습니다</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminUserManager;
